import { Delete, Star, StarBorder } from "@mui/icons-material";
import { Box, FormControl, IconButton, ListItemIcon, ListItemText, MenuItem, Select, TextField } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useState } from "react";
import ReactCountryFlag from "react-country-flag";
import countriesData from "../../../../../data/countriesList.json";
import { menuPropsModal } from "../../../../../utils";
import { colors } from "../../../../ui/colors";

export interface PhoneContactItem {
  id: string | number;
  countryCode: string;
  dialCode: string;
  phone: string;
  tag?: string;
  isDefault?: boolean;
}

interface InputMultipleContactsProps {
  sx?: SxProps<Theme>;
  item: PhoneContactItem;
  index: number;
  disabled?: boolean;
  error?: string;
  showErrors?: boolean;
  onChange: (updated: PhoneContactItem) => void;
  onRemove: () => void;
  onSetDefault: () => void;
}

export const formatUSPhone = (value: string) => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const InputMultipleContacts = ({
  sx,
  item,
  index,
  disabled = false,
  error = "",
  showErrors = false,
  onChange,
  onRemove,
  onSetDefault,
}: InputMultipleContactsProps) => {
  const countries = countriesData;
  const [isCodesOpen, setIsCodesOpen] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const countryValue = `${item.countryCode || "US"}-${item.dialCode || "+1"}`;

  const handleCountryCodeChange = (e: any) => {
    const selectedValue = e.target.value;
    const [code, dialCode] = selectedValue.split("-");
    onChange({
      ...item,
      countryCode: code,
      dialCode: dialCode,
    });
  };

  const handlePhoneTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTouched(true);
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    onChange({
      ...item,
      phone: digits,
    });
  };

  const hasError = !!(showErrors || isTouched) && !!error;
  const formattedDisplayValue = formatUSPhone(item.phone || "");

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      {/* Combined Input Group */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          flex: "1 1 220px",
          minWidth: "200px",
        }}
      >
        {/* Country Code Selector */}
        <FormControl
          sx={{
            width: 105,
            flexShrink: 0,
            mt: 0,
            "& .MuiOutlinedInput-root": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              minHeight: "unset",
              height: "48px",
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              "& fieldset": {
                borderColor: colors.orange.base,
                borderWidth: "1px",
                borderRightWidth: 0,
              },
              "&:hover fieldset": {
                borderColor: colors.orange[200],
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.orange.base,
                boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
              },
              "&.Mui-error fieldset": {
                borderColor: colors.danger.base,
              },
            },
            "& .MuiSelect-select": {
              padding: "12px 22px 12px 10px !important",
              fontSize: "0.9rem",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              color: colors.gray.base,
            },
            "& .MuiSelect-icon": {
              right: "2px",
            },
          }}
        >
          <Select
            MenuProps={menuPropsModal}
            value={countryValue}
            onChange={handleCountryCodeChange}
            onOpen={() => setIsCodesOpen(true)}
            onClose={() => setIsCodesOpen(false)}
            variant="outlined"
            disabled={disabled}
            renderValue={(selected) => {
              const [code, dialCode] = selected.split("-");
              const selectedCountry = countries.find((c) => c.code === code) || countries.find((c) => c.code === "US");

              return selectedCountry ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <ReactCountryFlag countryCode={selectedCountry.code} svg style={{ width: 20, height: 15, borderRadius: 2, objectFit: "cover" }} />
                  <Box component="span" sx={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--fx-surface-textsecondary, #64748B)" }}>
                    {dialCode}
                  </Box>
                </Box>
              ) : (
                ""
              );
            }}
          >
            {countries.map((c) => (
              <MenuItem key={c.code} value={`${c.code}-${c.dialCode}`}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ReactCountryFlag countryCode={c.code} svg style={{ width: 20, height: 15, borderRadius: 2, objectFit: "cover" }} />
                </ListItemIcon>
                <ListItemText
                  primary={isCodesOpen ? `${c.name} (${c.dialCode})` : c.dialCode}
                  primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Phone Input */}
        <TextField
          fullWidth
          placeholder="Phone number"
          type="tel"
          variant="outlined"
          inputMode="tel"
          value={formattedDisplayValue}
          onChange={handlePhoneTextChange}
          onBlur={() => setIsTouched(true)}
          error={hasError}
          helperText={hasError ? error : ""}
          disabled={disabled}
          sx={{
            flexGrow: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              paddingX: "4px",
              paddingY: "4px",
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              height: "48px",
              "& fieldset": {
                borderColor: colors.orange.base,
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: colors.orange[200],
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.orange.base,
                boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
              },
              "&.Mui-error fieldset": {
                borderColor: colors.danger.base,
              },
            },
            "& input": {
              padding: "12px 14px",
              fontSize: "1.0rem",
              color: colors.gray.base,
            },
          }}
        />
      </Box>

      {/* Actions Container */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flex: "0 0 auto",
          ml: "auto",
          height: "48px",
        }}
      >
        {/* Default Phone Indicator */}
        <IconButton
          onClick={() => !disabled && onSetDefault()}
          disabled={disabled}
          sx={{
            color: item.isDefault ? colors.orange.base : colors.gray[300],
            "&:hover": {
              color: colors.orange.base,
            },
          }}
          title="Mark as default phone"
        >
          {item.isDefault ? <Star sx={{ fontSize: 20 }} /> : <StarBorder sx={{ fontSize: 20 }} />}
        </IconButton>

        {!disabled && (
          <IconButton
            onClick={onRemove}
            size="small"
            sx={{
              p: 0.5,
              color: colors.danger[600],
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: colors.orange[50],
                borderColor: colors.orange[400],
              },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
